// ABOUTME: iOS-style tab bar component for mobile navigation
// ABOUTME: Provides three main sections: List, Add, and SmartQuote

/**
 * iOS Tab Bar Height Specifications:
 * - iPhone (portrait/landscape): 49pt
 * - iPhone (compact landscape): 32pt
 * - iPad: 50pt
 * - Safe area inset bottom: varies by device (0-34pt)
 * - Home indicator devices add ~34pt safe area
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { List, Plus, MessageSquareQuote, Check, Send, ArrowRight } from 'lucide-react';
import { hapticPatterns } from '@/lib/haptic';
import { motion, AnimatePresence } from 'framer-motion';

export type TabId = 'list' | 'add' | 'smartquote';

export interface TabBarActionButton {
  label: string;
  onClick: () => void;
  icon?: 'send' | 'check' | 'next';
  disabled?: boolean;
  variant?: 'primary' | 'success';
}

interface TabBarProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  className?: string;
  actionButton?: TabBarActionButton;
}

interface TabItem {
  id: TabId;
  icon: React.ReactNode;
  label: string;
}

const tabs: TabItem[] = [
  {
    id: 'list',
    icon: <List className="w-6 h-6" />,
    label: 'List'
  },
  {
    id: 'add',
    icon: (
      <div className="relative">
        <div className="w-7 h-7 bg-purple-600 rounded-full flex items-center justify-center">
          <Plus className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
      </div>
    ),
    label: 'Add'
  },
  {
    id: 'smartquote',
    icon: <MessageSquareQuote className="w-6 h-6" />,
    label: 'Quote'
  }
];

export const TabBar: React.FC<TabBarProps> = ({
  activeTab,
  onTabChange,
  className,
  actionButton
}) => {
  const handleTabPress = (tabId: TabId) => {
    if (tabId !== activeTab) {
      hapticPatterns.light();
      onTabChange(tabId);
    }
  };

  const handleActionPress = () => {
    if (actionButton && !actionButton.disabled) {
      hapticPatterns.medium();
      actionButton.onClick();
    }
  };

  // Get icon component for action button
  const getActionIcon = () => {
    switch (actionButton?.icon) {
      case 'send':
        return <Send className="h-4 w-4" />;
      case 'check':
        return <Check className="h-4 w-4" />;
      case 'next':
        return <ArrowRight className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="fixed bottom-10 left-0 right-0 z-50 flex items-center justify-center px-4"
      style={{
        marginBottom: 'env(safe-area-inset-bottom, 0px)'
      }}
    >
      {/* Action Button - appears to the left of tab bar */}
      <AnimatePresence>
        {actionButton && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, x: 20 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.8, x: 20 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            onClick={handleActionPress}
            disabled={actionButton.disabled}
            className={cn(
              'mr-3 px-5 py-3 rounded-full',
              'bg-white/40 backdrop-blur-xl',
              'shadow-xl border border-white/30',
              'flex items-center justify-center gap-2',
              'font-medium text-sm',
              'transition-all duration-200',
              'active:scale-95',
              actionButton.variant === 'success'
                ? 'text-purple-700 bg-purple-100/40'
                : 'text-purple-700',
              actionButton.disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {getActionIcon()}
            <span>{actionButton.label}</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Tab Bar */}
      <div
        className={cn(
          'w-64',
          'bg-white/40 backdrop-blur-xl',
          'rounded-full shadow-xl border border-white/30',
          className
        )}
      >
        <nav className="h-16 px-4 py-2">
          <ul className="flex h-full items-center justify-around">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const isAdd = tab.id === 'add';

            return (
              <li key={tab.id} className="flex-1 h-full">
                <button
                  type="button"
                  onClick={() => handleTabPress(tab.id)}
                  className={cn(
                    'w-full h-full flex flex-col items-center justify-center gap-1',
                    'transition-all duration-300 ease-out',
                    'active:scale-95',
                    'relative rounded-full px-2 py-2',
                    isActive && 'bg-white/80 shadow-sm'
                  )}
                  aria-label={tab.label}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <div
                    className={cn(
                      'transition-all duration-300',
                      isActive && !isAdd && 'text-purple-600 scale-110',
                      !isActive && !isAdd && 'text-gray-500'
                    )}
                  >
                    {tab.icon}
                  </div>
                  <span
                    className={cn(
                      'text-sm font-semibold transition-all duration-300',
                      isActive && 'text-purple-600 scale-105',
                      !isActive && 'text-gray-500'
                    )}
                  >
                    {tab.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
    </div>
  );
};
